import { addDays, isValidDate } from '../time/date';
import { epochOfWallMin, localCalendarDate, localMinuteOfDay } from '../time/personal-day';
import { readPlanHistory, readPlannedMin } from './checkin-plan';
import {
  readCheckinRecurrence,
  recurrenceOccursOn,
  type CheckinRecurrenceFields,
} from './checkin-recurrence';
import { CHECKIN_EARLY_DONE_MS, MINUTES_PER_DAY } from './constants';
import { sameJsonValue } from './revisions';

/**
 * One check-in occurrence = one dose an item is due at (data dictionary §3.2). An occurrence is
 * identified by its scheduled local date-time (or, for an item without a planned time, by its
 * date); the personal day only groups occurrences for display, so changing the day start never
 * changes which occurrence a completion belongs to. Pure date-string arithmetic: no time zone
 * is involved until a reminder needs an absolute instant (time-drift D3, D8, D10).
 */

/** `YYYY-MM-DDTHH:mm`, floating local time; string order is time order. */
export type LocalDateTime = string;

/** An occurrence key: a `LocalDateTime` for a timed occurrence, a `YYYY-MM-DD` date otherwise. */
export type OccurrenceKey = string;

const LOCAL_DATE_TIME = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})$/;

export function isLocalDateTime(value: unknown): value is LocalDateTime {
  if (typeof value !== 'string') return false;
  const match = LOCAL_DATE_TIME.exec(value);
  if (match === null) return false;
  const [, date, hours, minutes] = match;
  return isValidDate(date as string) && Number(hours) < 24 && Number(minutes) < 60;
}

/** `date` at `min` minutes after its 00:00 (0–1439). */
export function localDateTime(date: string, min: number): LocalDateTime {
  const hours = Math.floor(min / 60);
  const minutes = min % 60;
  return `${date}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** The local date-time of an instant on the current wall clock (minute precision). */
export function localDateTimeOf(epochMs: number): LocalDateTime {
  return localDateTime(localCalendarDate(epochMs), localMinuteOfDay(epochMs));
}

/** Date and minute of day of a `LocalDateTime`. */
export function splitLocalDateTime(value: LocalDateTime): { readonly date: string; readonly min: number } {
  return { date: value.slice(0, 10), min: Number(value.slice(11, 13)) * 60 + Number(value.slice(14, 16)) };
}

/** Whether a key names a timed occurrence (otherwise it is a date). */
export const isTimedKey = (key: OccurrenceKey): boolean => key.length > 10;

/** The personal date a `LocalDateTime` belongs to under day start `dayStartMin`. */
export function personalDateOfLocal(value: LocalDateTime, dayStartMin: number): string {
  const { date, min } = splitLocalDateTime(value);
  return min < dayStartMin ? addDays(date, -1) : date;
}

/** The personal date an occurrence key belongs to: its date for an untimed key. */
export function personalDateOfKey(key: OccurrenceKey, dayStartMin: number): string {
  return isTimedKey(key) ? personalDateOfLocal(key, dayStartMin) : key;
}

/** Epoch ms of a `LocalDateTime` on the current local wall clock (DST gaps move forward). */
export function epochOfLocalDateTime(value: LocalDateTime): number {
  const { date, min } = splitLocalDateTime(value);
  return epochOfWallMin(date, min);
}

/** A planned time relative to the rule day: `dayOffset = 1` is the calendar day after it. */
export interface SlotTime {
  readonly min: number;
  readonly dayOffset: 0 | 1;
}

/** One version of an item's plan (data dictionary §3.2). */
export interface ScheduleVersion {
  /** First local date-time the version owns; null = from the beginning. */
  readonly from: LocalDateTime | null;
  /** Planned times; empty = no planned time. At most one in this release. */
  readonly times: readonly SlotTime[];
  /** Day start when the version was written: places its untimed occurrences. */
  readonly dayStartMin: number;
}

/** The legacy fields written alongside `schedule`, to notice an older client's later edit. */
export interface LegacyPlanSnapshot {
  readonly plannedMin: number | null;
  readonly planHistory: unknown;
}

/** What the occurrence helpers read from a check-in item. */
export interface OccurrenceItemData extends CheckinRecurrenceFields {
  readonly plannedMin?: unknown;
  readonly planHistory?: unknown;
  readonly schedule?: unknown;
  readonly disabledAt?: unknown;
}

const isDayMinute = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < MINUTES_PER_DAY;

/** The planned times for `plannedMin` under day start `dayStartMin`: earlier than it means the next day. */
export function slotTimesOf(plannedMin: number | null, dayStartMin: number): SlotTime[] {
  return plannedMin === null ? [] : [{ min: plannedMin, dayOffset: plannedMin < dayStartMin ? 1 : 0 }];
}

function readVersion(value: unknown): ScheduleVersion | null {
  if (typeof value !== 'object' || value === null) return null;
  const { from, times, dayStartMin } = value as Record<string, unknown>;
  if (from !== null && !isLocalDateTime(from)) return null;
  if (!isDayMinute(dayStartMin) || !Array.isArray(times)) return null;
  const slots: SlotTime[] = [];
  for (const time of times as unknown[]) {
    if (typeof time !== 'object' || time === null) return null;
    const { min, dayOffset } = time as Record<string, unknown>;
    if (!isDayMinute(min) || (dayOffset !== 0 && dayOffset !== 1)) return null;
    slots.push({ min, dayOffset });
  }
  return { from: from as LocalDateTime | null, times: slots.slice(0, 1), dayStartMin };
}

/** The stored `schedule` field, or null when absent or not readable as a whole. */
export function readStoredSchedule(
  value: unknown,
): { readonly versions: ScheduleVersion[]; readonly legacy: LegacyPlanSnapshot } | null {
  if (typeof value !== 'object' || value === null) return null;
  const { versions, legacy } = value as Record<string, unknown>;
  if (!Array.isArray(versions) || versions.length === 0) return null;
  const read: ScheduleVersion[] = [];
  for (const entry of versions as unknown[]) {
    const version = readVersion(entry);
    if (version === null) return null;
    const previous = read.at(-1);
    if (
      previous === undefined
        ? version.from !== null
        : version.from === null || version.from <= (previous.from ?? '')
    ) {
      return null;
    }
    read.push(version);
  }
  if (typeof legacy !== 'object' || legacy === null) return null;
  const snapshot = legacy as Record<string, unknown>;
  return {
    versions: read,
    legacy: { plannedMin: readPlannedMin(snapshot), planHistory: snapshot['planHistory'] ?? null },
  };
}

/** The anchor of an occurrence: its planned local date-time, or the rule day's start when untimed. */
function anchorOf(ruleDay: string, slot: SlotTime | undefined, dayStartMin: number): LocalDateTime {
  return slot === undefined
    ? localDateTime(ruleDay, dayStartMin)
    : localDateTime(slot.dayOffset === 1 ? addDays(ruleDay, 1) : ruleDay, slot.min);
}

/**
 * The versions an item without a readable `schedule` has (data dictionary §3.2): `planHistory`
 * then `plannedMin`, each next version starting at min(B 00:00, its first occurrence on the first
 * personal day the old model gave it), with `legacyDayStartMin` as the day start. Every
 * occurrence the old model showed stays; the one it lost across the day start reappears.
 */
export function deriveScheduleFromLegacy(
  data: OccurrenceItemData,
  legacyDayStartMin: number,
): ScheduleVersion[] {
  const start = legacyDayStartMin;
  const history = readPlanHistory(data);
  const planned = [...history.map((version) => version.plannedMin), readPlannedMin(data)];
  const versions: ScheduleVersion[] = [];
  planned.forEach((plannedMin, index) => {
    const times = slotTimesOf(plannedMin, start);
    if (index === 0) {
      versions.push({ from: null, times, dayStartMin: start });
      return;
    }
    const closed = history[index - 1]!;
    const before = closed.plannedMin;
    const firstDay = before !== null && before < start ? addDays(closed.before, -1) : closed.before;
    const firstNew = anchorOf(firstDay, times[0], start);
    const boundary = localDateTime(closed.before, 0);
    versions.push({ from: firstNew < boundary ? firstNew : boundary, times, dayStartMin: start });
  });
  return versions;
}

/** The legacy fields as the snapshot compares them (an absent field reads as null). */
const legacySnapshotOf = (data: OccurrenceItemData): LegacyPlanSnapshot => ({
  plannedMin: readPlannedMin(data),
  planHistory: data.planHistory ?? null,
});

/**
 * The plan versions of an item: the stored `schedule` while the legacy fields still match its
 * snapshot, otherwise derived from the legacy fields (an older client edited them, or the item
 * predates `schedule`).
 */
export function readCheckinSchedule(data: OccurrenceItemData, legacyDayStartMin: number): ScheduleVersion[] {
  const stored = readStoredSchedule(data.schedule);
  if (stored !== null && sameJsonValue(stored.legacy, legacySnapshotOf(data))) return stored.versions;
  return deriveScheduleFromLegacy(data, legacyDayStartMin);
}

/** A scheduled occurrence before completion state is attached. */
export interface OccurrenceSlot {
  readonly key: OccurrenceKey;
  /** Planned local date-time; null for an untimed occurrence. */
  readonly occursAt: LocalDateTime | null;
  /** Date label of an untimed occurrence; null for a timed one. */
  readonly date: string | null;
  readonly ruleDay: string;
  readonly plannedMin: number | null;
  /** Planned local date-time, or the rule day's start for an untimed occurrence: orders and places it. */
  readonly anchor: LocalDateTime;
  /** Index of the plan version it belongs to. */
  readonly version: number;
}

/** The slots of each version on `ruleDay` that fall inside that version, ignoring the rule. */
function versionSlotsOn(versions: readonly ScheduleVersion[], ruleDay: string): OccurrenceSlot[] {
  const slots: OccurrenceSlot[] = [];
  versions.forEach((version, index) => {
    const until = versions[index + 1]?.from ?? null;
    const times: readonly (SlotTime | undefined)[] = version.times.length === 0 ? [undefined] : version.times;
    for (const time of times) {
      const anchor = anchorOf(ruleDay, time, version.dayStartMin);
      if (version.from !== null && anchor < version.from) continue;
      if (until !== null && anchor >= until) continue;
      slots.push({
        key: time === undefined ? ruleDay : anchor,
        occursAt: time === undefined ? null : anchor,
        date: time === undefined ? ruleDay : null,
        ruleDay,
        plannedMin: time === undefined ? null : time.min,
        anchor,
        version: index,
      });
    }
  });
  return slots;
}

/** Whether a disabled item hides this occurrence (data dictionary §3.2). */
function hiddenByDisable(data: OccurrenceItemData, slot: OccurrenceSlot): boolean {
  if (data.active !== false) return false;
  if (isLocalDateTime(data.disabledAt)) return slot.anchor >= data.disabledAt;
  const from =
    typeof data.disabledFrom === 'string' && isValidDate(data.disabledFrom) ? data.disabledFrom : null;
  return from === null || slot.ruleDay >= from;
}

/** The occurrences due on rule day `ruleDay`: the rule hits, inside a version, not disabled. */
export function occurrencesOnRuleDay(
  data: OccurrenceItemData,
  versions: readonly ScheduleVersion[],
  ruleDay: string,
): OccurrenceSlot[] {
  if (!recurrenceOccursOn(readCheckinRecurrence(data), ruleDay)) return [];
  return versionSlotsOn(versions, ruleDay).filter((slot) => !hiddenByDisable(data, slot));
}

/**
 * The occurrences of personal day `personalDate` under day start `dayStartMin`: timed ones whose
 * planned local date-time is in [D start, D+1 start), untimed ones dated D. Ascending by anchor.
 */
export function occurrencesInPersonalDay(
  data: OccurrenceItemData,
  versions: readonly ScheduleVersion[],
  personalDate: string,
  dayStartMin: number,
): OccurrenceSlot[] {
  const from = localDateTime(personalDate, dayStartMin);
  const until = localDateTime(addDays(personalDate, 1), dayStartMin);
  const found: OccurrenceSlot[] = [];
  for (const ruleDay of [addDays(personalDate, -1), personalDate, addDays(personalDate, 1)]) {
    for (const slot of occurrencesOnRuleDay(data, versions, ruleDay)) {
      const inDay =
        slot.occursAt === null ? slot.date === personalDate : slot.anchor >= from && slot.anchor < until;
      if (inDay) found.push(slot);
    }
  }
  return found.sort((a, b) => (a.anchor < b.anchor ? -1 : a.anchor > b.anchor ? 1 : 0));
}

/** The completion fields that locate a check-in log. */
export interface OccurrenceLogFields {
  readonly personalDate: string;
  readonly occursAt?: unknown;
  readonly date?: unknown;
}

/**
 * The occurrence a completion record belongs to. A record with `occursAt` or `date` names it; a
 * legacy record (only `personalDate`) belongs to the earliest version's occurrence on that rule
 * day, rule and disabling aside (the old model's one occurrence of the day); with none, to its date.
 */
export function logOccurrenceKey(
  log: OccurrenceLogFields,
  versions: readonly ScheduleVersion[],
): OccurrenceKey {
  if (isLocalDateTime(log.occursAt)) return log.occursAt;
  if (typeof log.date === 'string' && isValidDate(log.date)) return log.date;
  return versionSlotsOn(versions, log.personalDate)[0]?.key ?? log.personalDate;
}

/** Whether a completion record is a legacy one (located by `personalDate` only). */
export const isLegacyLog = (log: OccurrenceLogFields): boolean =>
  !isLocalDateTime(log.occursAt) && !(typeof log.date === 'string' && isValidDate(log.date));

/**
 * The versions after changing the planned time to `plannedMin` from `from` on (data dictionary
 * §3.2): versions starting at or after `from` are dropped, the new one appended and merged into
 * an equal predecessor. `from = null` (every date) leaves the new version alone.
 */
export function scheduleAfterChange(
  versions: readonly ScheduleVersion[],
  from: LocalDateTime | null,
  plannedMin: number | null,
  dayStartMin: number,
): ScheduleVersion[] {
  const next: ScheduleVersion = { from, times: slotTimesOf(plannedMin, dayStartMin), dayStartMin };
  if (from === null) return [next];
  const kept = versions.filter((version) => version.from === null || version.from < from);
  const previous = kept.at(-1);
  // Equal planned times merge even when written under another day start (a withdrawn change
  // restores the earlier version as it was).
  const minutes = (version: ScheduleVersion) => version.times.map((time) => time.min);
  if (previous !== undefined && sameJsonValue(minutes(previous), minutes(next))) return kept;
  return [...kept, next];
}

/**
 * The legacy fields projected from `versions` for older clients: `plannedMin` = the last
 * version's time, `planHistory` = one closed entry per boundary (approximate: the boundary's date).
 */
export function legacyProjectionOf(versions: readonly ScheduleVersion[]): LegacyPlanSnapshot {
  const timeOf = (version: ScheduleVersion): number | null => version.times[0]?.min ?? null;
  const history = versions.slice(1).flatMap((version, index) => {
    const before = (version.from as LocalDateTime).slice(0, 10);
    return [{ before, plannedMin: timeOf(versions[index]!) }];
  });
  // Entries must have strictly ascending dates to be readable; keep the last of each date.
  const readable = history.filter((entry, index) => history[index + 1]?.before !== entry.before);
  return { plannedMin: timeOf(versions.at(-1)!), planHistory: readable.length === 0 ? null : readable };
}

/** The `schedule` value to store for `versions`, with the snapshot of the projected legacy fields. */
export function scheduleFieldOf(versions: readonly ScheduleVersion[]): {
  readonly versions: readonly ScheduleVersion[];
  readonly legacy: LegacyPlanSnapshot;
} {
  return { versions, legacy: legacyProjectionOf(versions) };
}

/** The version in effect at `at` (the last whose `from` is not later). */
export function versionAt(versions: readonly ScheduleVersion[], at: LocalDateTime): ScheduleVersion {
  let found = versions[0]!;
  for (const version of versions) if (version.from === null || version.from <= at) found = version;
  return found;
}

/**
 * The next `count` occurrences of planned time `plannedMin` from `after` on (inclusive), by the
 * item's rule (disabling aside): the choices "from which one" of a planned-time change.
 */
export function upcomingOccurrencesOf(
  data: OccurrenceItemData,
  plannedMin: number | null,
  after: LocalDateTime,
  dayStartMin: number,
  count: number,
): OccurrenceSlot[] {
  const versions: ScheduleVersion[] = [
    { from: null, times: slotTimesOf(plannedMin, dayStartMin), dayStartMin },
  ];
  const rule = readCheckinRecurrence(data);
  const found: OccurrenceSlot[] = [];
  const horizon = 400 * Math.max(count, 1);
  let ruleDay = addDays(after.slice(0, 10), -1);
  for (let i = 0; i < horizon && found.length < count; i += 1, ruleDay = addDays(ruleDay, 1)) {
    if (rule.endDate !== null && ruleDay > rule.endDate) break;
    if (!recurrenceOccursOn(rule, ruleDay)) continue;
    for (const slot of versionSlotsOn(versions, ruleDay)) if (slot.anchor >= after) found.push(slot);
  }
  return found.slice(0, count);
}

/**
 * The rule day an occurrence key belongs to under `versions` (the day whose rule produced it),
 * or null when no version produces that key: an untimed key is its own rule day; a timed one is
 * its date or the day before (a planned time after midnight).
 */
export function ruleDayOfKey(versions: readonly ScheduleVersion[], key: OccurrenceKey): string | null {
  if (!isTimedKey(key)) return key;
  const date = key.slice(0, 10);
  for (const ruleDay of [date, addDays(date, -1)]) {
    if (versionSlotsOn(versions, ruleDay).some((slot) => slot.key === key)) return ruleDay;
  }
  return null;
}

/** The occurrence key `versions` give rule day `ruleDay` (the first one), or null when none. */
export function keyOnRuleDay(versions: readonly ScheduleVersion[], ruleDay: string): OccurrenceKey | null {
  return versionSlotsOn(versions, ruleDay)[0]?.key ?? null;
}

/**
 * Earliest completion instant of an occurrence (data dictionary §3, checkinLog.doneAt): the start
 * of its personal day, or for a timed one CHECKIN_EARLY_DONE_MS before its planned time when that
 * is earlier.
 */
export function earliestDoneAtOf(key: OccurrenceKey, dayStartMin: number): number {
  const dayStart = epochOfWallMin(personalDateOfKey(key, dayStartMin), dayStartMin);
  return isTimedKey(key) ? Math.min(dayStart, epochOfLocalDateTime(key) - CHECKIN_EARLY_DONE_MS) : dayStart;
}
